import React, { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import Dropdown, { OptionType } from 'components/Dropdown'
import { DropDownItem } from 'types/components'
import { Form, Formik } from 'formik'
import Input from 'components/Input'
import SearchBar from 'components/SearchBar/SearchBar'

import {
  MerchantLookupTypes,
  MerchantLookupTxSetFieldTypes,
} from 'types/merchanTxTypes'
import { MerchantLookupTxSchemas } from 'schemas/merchantTxSchemas'
import { useMerchantLookupTx as useMerchantLookupWithTraderId } from './useMerchantLookupTx'
import { useMerchantLookupTx as useMerchantLookupListOnly } from './useMerchantLookupListsTx'
const Index: React.FC<{
  setFieldValue: MerchantLookupTxSetFieldTypes
  title?: string
  placeholder?: string
  useMerchantLookupLists?: boolean
}> = ({
  setFieldValue: setFieldValueMain,
  title,
  placeholder,
  useMerchantLookupLists = false,
}) => {
  const { traderId } = useParams()
  const [selectedMerchant, setSelectedMerchant] = useState<string>('') // Stores merchant name for display
  const [selectedTraderId, setSelectedTraderId] = useState<string>('') // Stores traderId for form submission
  const [selectedMerchantId, setSelectedMerchantId] = useState<number | null>(null)
  const [advancedResults, setAdvancedResults] = useState<MerchantLookupTypes[]>([])
  const [isAdvancedSearching, setIsAdvancedSearching] = useState(false)
  const [advancedSearchError, setAdvancedSearchError] = useState('')
  const merchantLookupWithTraderId = useMerchantLookupWithTraderId(
    String(traderId),
    setFieldValueMain
  )
  const merchantLookupListOnly = useMerchantLookupListOnly(setFieldValueMain)
  const {
    initialValues,
    handleSubmitMerchantLookupTx,
    merchantLookupDropdownOptions,
    searchMerchants,
  } = useMerchantLookupLists
    ? merchantLookupListOnly
    : merchantLookupWithTraderId

  // start: Merchant lookup dropdown options handler
  const merchantLoadOptions = useCallback(
    async (inputValue: string): Promise<DropDownItem[]> => {
      return merchantLookupDropdownOptions(inputValue)
    },
    [merchantLookupDropdownOptions]
  )

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={MerchantLookupTxSchemas}
      onSubmit={handleSubmitMerchantLookupTx}
    >
      {({ setFieldValue }) => (
        <Form className="flex flex-col">
          <div className="mb-0">
            {useMerchantLookupLists ? (
              <Dropdown
                title={title || 'Merchant Lookup'}
                value={selectedMerchant}
                placeholder={placeholder || 'Search by merchant name...'}
                loadOptions={merchantLoadOptions}
                onChange={(option: OptionType) => {
                  const merchant = String(option?.value).split('/')
                  const merchantTraderId = merchant[0]
                  const deviceCode = merchant[1]
                  setFieldValueMain('storeId', merchantTraderId)
                  setFieldValueMain('deviceCode', deviceCode)
                  setFieldValueMain('fromTraderId', Number(merchantTraderId))
                  setSelectedMerchantId(Number(merchantTraderId))
                  setSelectedTraderId(merchantTraderId)
                  setSelectedMerchant(option?.label || '')
                }}
                className="w-full"
                borderColor="#C4C4C4"
                containerClassName="focus:border-primary"
                isSearchable
                async
              />
            ) : (
              <>
                <p className="font-light tracking-wide">Merchant Lookup</p>
                <div className="mt-3">
                  <SearchBar
                    isMerchantLookup
                    placeholder="Search merchants..."
                    startLoading={() => {
                      setIsAdvancedSearching(true)
                      setAdvancedSearchError('')
                      setAdvancedResults([])
                    }}
                    stopLoading={() => setIsAdvancedSearching(false)}
                    startFetch={async (params) => {
                      try {
                        const results = await searchMerchants(params)
                        setAdvancedResults(results)
                        setAdvancedSearchError(
                          results.length ? '' : 'No results found'
                        )
                      } catch {
                        setAdvancedResults([])
                        setAdvancedSearchError('No results found')
                      } finally {
                        setIsAdvancedSearching(false)
                      }
                    }}
                    closeSearchForm={() => {
                      // avoid typescript-eslint no-empty
                    }}
                  />
                  {isAdvancedSearching && (
                    <div className="mt-1 text-sm text-gray-600">Loading...</div>
                  )}
                  {advancedSearchError && (
                    <div className="mt-1 text-sm text-red-600">
                      {advancedSearchError}
                    </div>
                  )}
                  {advancedResults.length > 0 && (
                    <div className="max-h-60 overflow-y-auto border rounded mt-2">
                      <table className="min-w-full text-sm">
                        <thead className="sticky top-0 bg-gray-100">
                          <tr>
                            <th className="px-2 py-1 text-left">Merchant Name</th>
                            <th className="px-2 py-1 text-left">Device Code</th>
                          </tr>
                        </thead>
                        <tbody>
                          {advancedResults.map((merchant) => (
                            <tr
                              key={merchant.traderId}
                              className={`cursor-pointer hover:bg-gray-50 ${
                                selectedMerchantId === merchant.traderId
                                  ? 'bg-blue-100'
                                  : ''
                              }`}
                              onClick={() => {
                                setFieldValueMain('storeId', merchant.traderId)
                                setFieldValueMain(
                                  'deviceCode',
                                  String(merchant.deviceCode || '')
                                )
                                setFieldValueMain('fromTraderId', merchant.traderId)
                                setSelectedTraderId(String(merchant.traderId))
                                setSelectedMerchantId(merchant.traderId)
                              }}
                            >
                              <td className="px-2 py-1">{merchant.merchantName}</td>
                              <td className="px-2 py-1">{merchant.deviceCode}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <Input
            type="hidden"
            name="enrollmentTraderId"
            value={selectedTraderId}
          />
        </Form>
      )}
    </Formik>
  )
}

export default Index
